import { Typography } from '@mui/material';
import { GA } from '@services/gtag';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Toast } from 'src/components/Toast';
import Toggle from 'src/components/Toggle';
import { useConfiguration } from 'src/providers/Configuration';
import { useTenant } from 'src/providers/Tenant';
import { ColourMode, Configuration, patchConfiguration } from 'src/services/productInventory';

interface ColourToggleProps {
  configurationPID: string;
}

export function ColourToggle({ configurationPID }: Readonly<ColourToggleProps>) {
  // const { accountId } = useTenant();?
  const tenant = useTenant();
  const queryClient = useQueryClient();
  const { colourMode } = useConfiguration();
  const [isProductColour, setIsProductColour] = useState(colourMode === undefined || colourMode === ColourMode.COLOUR);
  const { t } = useTranslation('manageProduct');
  const { name: productName } = useConfiguration();

  const {
    mutate: handleColourMode,
    isLoading: isUpdatingColourMode,
    isError: isErrorColour,
  } = useMutation(
    async (body: Partial<Configuration & { colourMode: ColourMode.COLOUR | ColourMode.GREYSCALE }>) => {
      await patchConfiguration(configurationPID, body, tenant.accountId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['getConfiguration']);
        queryClient.invalidateQueries(['productList']);
      },
    }
  );

  const handleColourToggleChange = async (e: MouseEvent) => {
    const colourMode = (e.target as HTMLInputElement).checked ? ColourMode.COLOUR : ColourMode.GREYSCALE;

    handleColourMode({ colourMode });
    setIsProductColour((prevState) => !prevState);
    if (colourMode === ColourMode.GREYSCALE) {
      GA.ManageProductPage.greyscaleEnabled(tenant, productName, colourMode);
    }
    if (colourMode === ColourMode.COLOUR) {
      GA.ManageProductPage.colourModeEnabled(tenant, productName, colourMode);
    }
  };

  const isDeterminingInitialColour = colourMode === undefined;

  /* istanbul ignore next - not sure we do need this really */
  if (isDeterminingInitialColour) {
    return null;
  }

  return (
    <>
      <Toggle
        loading={isUpdatingColourMode}
        checked={isProductColour}
        label={t('productOverview.turnOnColours')}
        states={{ checkedState: 'Yes', unCheckedState: 'No' }}
        onClick={handleColourToggleChange}
        id="productColourMode"
      />

      <Typography>{t('productOverview.productColour')}</Typography>

      <Toast dependency={isErrorColour} severity="error" title={t('productOverview.colourUpdateFailed')} />
    </>
  );
}
